import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DoorOpen, CheckCircle2, XCircle, FileWarning, ShieldCheck, Loader2, Check, X, AlertTriangle, ExternalLink } from "lucide-react";

const STATE_BADGE = {
  not_started: { cls: "bg-slate-100 text-slate-500", label: "Non avviato" },
  in_progress: { cls: "bg-blue-100 text-blue-700", label: "In corso" },
  submitted: { cls: "bg-indigo-100 text-indigo-700", label: "Inviato" },
  under_review: { cls: "bg-amber-100 text-amber-800", label: "In verifica" },
  approved: { cls: "bg-emerald-100 text-emerald-700", label: "Approvato" },
  rejected: { cls: "bg-rose-100 text-rose-700", label: "Rifiutato" },
  missing_data: { cls: "bg-orange-100 text-orange-800", label: "Dati mancanti" },
  access_ready: { cls: "bg-emerald-600 text-white", label: "Accesso pronto" },
};
const RESOLVE_LINK = { qr: { label: "Vai al QR Center", route: "/qr-center" } };

function CondRow({ c, onResolve }) {
  const icon = c.status === "met"
    ? <Check size={15} className="mt-0.5 shrink-0 text-emerald-600" />
    : c.status === "pending"
      ? <AlertTriangle size={15} className="mt-0.5 shrink-0 text-amber-500" />
      : <X size={15} className="mt-0.5 shrink-0 text-rose-400" />;
  const txt = c.status === "met" ? "text-slate-700" : c.status === "pending" ? "text-amber-700" : "text-slate-500";
  const link = RESOLVE_LINK[c.resolve];
  return (
    <div className="flex items-start gap-1.5 text-xs" data-testid={`checkin-cond-${c.key}`}>
      {icon}
      <div className="min-w-0">
        <span className={txt}>{c.label}</span>
        {c.status !== "met" && c.reason && <p className="text-[11px] text-slate-400">{c.reason}</p>}
        {c.status !== "met" && link && (
          <button type="button" onClick={() => onResolve(link.route)} data-testid={`checkin-resolve-${c.key}`}
            className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-indigo-600 hover:underline">
            {link.label} <ExternalLink size={11} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function CheckinWorkflowPanel({ bookingId, onChange }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cond, setCond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [reasonFor, setReasonFor] = useState("");
  const [reason, setReason] = useState("");
  const [confirmGrant, setConfirmGrant] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/checkin/state/${bookingId}`),
      api.get(`/checkin/${bookingId}/access-conditions`),
    ]).then(([s, c]) => { setData(s.data); setCond(c.data); })
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [bookingId]);
  useEffect(() => { load(); }, [load]);

  const act = async (endpoint, body, label) => {
    setBusy(label);
    try {
      await api.post(`/checkin/${bookingId}/${endpoint}`, body || {});
      toast.success(label + " ✓");
      setReasonFor(""); setReason(""); setConfirmGrant(false);
      load(); onChange?.();
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.message) {
        const labels = (det.missing_details || []).map((m) => m.label).join(", ") || (det.missing_conditions || []).join(", ");
        toast.error(labels ? `${det.message}: ${labels}` : det.message);
      } else toast.error(det || "Errore");
    } finally { setBusy(""); }
  };

  if (loading) return <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 size={16} className="animate-spin" /> Caricamento check-in…</div>;
  if (!data) return null;

  const st = STATE_BADGE[data.checkin_state] || STATE_BADGE.not_started;
  const conds = cond?.conditions || [];
  const outcome = cond?.outcome;
  const canApprove = data.checkin_state === "under_review" || data.checkin_state === "missing_data";
  const canGrant = cond?.all_met && data.checkin_state !== "access_ready";
  const reasonRequired = reasonFor === "reject" || reasonFor === "request";

  return (
    <div data-testid="checkin-workflow-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-semibold text-slate-700"><DoorOpen size={16} className="text-indigo-600" /> Check-in Workflow</div>
        <Badge className={st.cls} data-testid="checkin-state-badge">{st.label}</Badge>
      </div>

      {/* Condizioni preliminari + esito */}
      <div className="mb-3 rounded-lg border border-slate-100 bg-slate-50/60 p-3" data-testid="checkin-conditions">
        <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-slate-400">Condizioni per l'accesso</p>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {conds.map((c) => <CondRow key={c.key} c={c} onResolve={(r) => navigate(r)} />)}
        </div>
        {outcome && (
          <div className="mt-2 border-t border-slate-200/70 pt-2">
            <CondRow c={outcome} onResolve={(r) => navigate(r)} />
          </div>
        )}
      </div>
      {cond?.journey && (
        <p className="mb-3 text-xs text-slate-400">Customer Journey: {cond.journey["CM-006_applicable"] ? "CM-006 attivo" : "CM-006 non necessario"} · {cond.journey["CM-007_applicable"] ? "CM-007 pronto" : "CM-007 non ancora"}</p>
      )}

      {/* Reason box (rifiuto / richiesta integrazione) */}
      {reasonFor && (
        <div className="mb-2" data-testid="checkin-reason-box">
          <Textarea data-testid="checkin-reason-input" placeholder={reasonFor === "reject" ? "Motivo del rifiuto (obbligatorio)…" : "Dati mancanti / motivo (obbligatorio)…"}
            value={reason} onChange={(e) => setReason(e.target.value)} className="h-16 text-sm" />
          <div className="mt-1 flex gap-2">
            <Button size="sm" data-testid="checkin-reason-confirm" disabled={!!busy || (reasonRequired && !reason.trim())}
              onClick={() => reasonFor === "reject"
                ? act("reject-documents", { reason }, "Documenti rifiutati")
                : act("request-integration", { reason, missing_data: reason ? [reason] : [] }, "Integrazione richiesta")}>
              {reasonFor === "reject" ? "Conferma rifiuto" : "Conferma richiesta"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setReasonFor(""); setReason(""); }}>Annulla</Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" data-testid="checkin-approve-btn"
          disabled={!canApprove || !!busy} onClick={() => act("approve-documents", { reason: "documenti approvati" }, "Documenti approvati")}>
          {busy === "Documenti approvati" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Approva documenti
        </Button>
        <Button size="sm" variant="outline" className="gap-1.5 text-rose-600" data-testid="checkin-reject-btn"
          disabled={!canApprove || !!busy} onClick={() => { setReasonFor("reject"); setReason(""); }}><XCircle size={14} /> Rifiuta</Button>
        <Button size="sm" variant="outline" className="gap-1.5 text-amber-700" data-testid="checkin-request-btn"
          disabled={!canApprove || !!busy} onClick={() => { setReasonFor("request"); setReason(""); }}><FileWarning size={14} /> Richiedi integrazione</Button>
        <Button size="sm" className="gap-1.5" data-testid="checkin-grant-btn"
          disabled={!canGrant || !!busy} onClick={() => setConfirmGrant(true)}>
          {busy === "Accesso reso disponibile" ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Rendi accesso disponibile
        </Button>
      </div>

      {/* Conferma azione sensibile: rendi accesso disponibile */}
      <AlertDialog open={confirmGrant} onOpenChange={setConfirmGrant}>
        <AlertDialogContent data-testid="checkin-grant-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Rendere l'accesso disponibile?</AlertDialogTitle>
            <AlertDialogDescription>
              Autorizzerai l'accesso dell'ospite ({data.guest_name || "ospite"}) e lo stato passerà ad «Accesso pronto».
              L'azione viene registrata in Timeline e Audit Log. Le istruzioni di accesso (CM-007) diventeranno inviabili.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="checkin-grant-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="checkin-grant-confirm-btn" className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => act("grant-access", {}, "Accesso reso disponibile")}>Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Storico stati */}
      {data.history?.length > 0 && (
        <div className="mt-3 border-t border-slate-100 pt-2" data-testid="checkin-history">
          <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-slate-400">Storico</p>
          <div className="space-y-1">
            {data.history.slice(-6).map((h) => (
              <p key={h.id} className="text-xs text-slate-500">
                <span className="text-slate-400">{new Date(h.at).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                {" · "}{h.from_state} → <b className="text-slate-700">{h.to_state}</b>{h.actor && h.actor !== "system" ? ` · ${h.actor}` : ` · ${h.origin}`}{h.reason ? ` — ${h.reason}` : ""}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
